import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Star, Check, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import api, { mensajeDeError } from '../lib/api';
import { toast } from 'sonner';

/**
 * Valoración de una sesión.
 *
 * Se entra por un enlace con token, sin iniciar sesión. La valoración queda
 * separada de quién la escribió: el profesional ve la nota y el comentario,
 * nunca de qué paciente vienen.
 */
const ValorarPage = () => {
  const { token } = useParams();

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [puntaje, setPuntaje] = useState(0);
  const [sobrePuntaje, setSobrePuntaje] = useState(0);
  const [comentario, setComentario] = useState('');

  useEffect(() => {
    let vigente = true;

    api
      .get(`/valoraciones/${token}`)
      .then(({ data }) => {
        if (vigente) setDatos(data);
      })
      .catch((err) => {
        if (vigente) toast.error(mensajeDeError(err, 'Ese enlace no es válido'));
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, [token]);

  const enviar = async (evento) => {
    evento.preventDefault();

    if (puntaje === 0) {
      toast.error('Elige cuántas estrellas le das a la sesión');
      return;
    }

    setEnviando(true);
    try {
      await api.post(`/valoraciones/${token}`, { score: puntaje, comment: comentario });
      setListo(true);
    } catch (err) {
      toast.error(mensajeDeError(err, 'No pudimos registrar tu valoración'));
    } finally {
      setEnviando(false);
    }
  };

  if (cargando) {
    return <div className="py-24 text-center text-slate-500">Cargando…</div>;
  }

  if (!datos) {
    return (
      <div className="py-24 text-center px-4">
        <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
          Ese enlace no es válido
        </h1>
        <p className="text-slate-600 mb-6">Puede que ya haya sido usado o esté mal copiado.</p>
        <Link to="/">
          <Button variant="outline" className="rounded-full">
            Volver al inicio
          </Button>
        </Link>
      </div>
    );
  }

  if (listo || datos.already_rated) {
    return (
      <div className="py-24 text-center px-4">
        <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-5">
          <Check className="w-7 h-7 text-teal-700" />
        </div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
          {listo ? '¡Gracias por tu valoración!' : 'Esta sesión ya fue valorada'}
        </h1>
        <p className="text-slate-600 mb-6 max-w-md mx-auto">
          Tu opinión ayuda a otras personas a elegir con más confianza.
        </p>
        <Link to="/mis-horas">
          <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8">
            Ir a mis horas
          </Button>
        </Link>
      </div>
    );
  }

  const fecha = datos.scheduled_date ? parseISO(datos.scheduled_date) : null;

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-semibold text-slate-900">
            ¿Cómo estuvo tu sesión?
          </h1>
          <p className="text-slate-600 mt-2">
            Con {datos.psychologist_name}
            {fecha && `, el ${format(fecha, "d 'de' MMMM", { locale: es })}`}
          </p>
        </div>

        <Card className="border-slate-200">
          <CardContent className="pt-8">
            <form onSubmit={enviar}>
              <div className="flex justify-center gap-2 mb-8">
                {[1, 2, 3, 4, 5].map((valor) => (
                  <button
                    key={valor}
                    type="button"
                    onClick={() => setPuntaje(valor)}
                    onMouseEnter={() => setSobrePuntaje(valor)}
                    onMouseLeave={() => setSobrePuntaje(0)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${valor} ${valor === 1 ? 'estrella' : 'estrellas'}`}
                    data-testid={`estrella-${valor}`}
                  >
                    <Star
                      className={`w-10 h-10 transition-colors ${
                        valor <= (sobrePuntaje || puntaje)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-slate-300'
                      }`}
                    />
                  </button>
                ))}
              </div>

              <Label htmlFor="comentario" className="text-slate-700">
                ¿Quieres contarnos algo más? (opcional)
              </Label>
              <Textarea
                id="comentario"
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Lo que quieras compartir sobre cómo te sentiste."
                rows={4}
                maxLength={1000}
                className="mt-2"
                data-testid="comentario"
              />

              <div className="flex gap-2 mt-4 p-3 rounded-xl bg-slate-50 text-sm text-slate-600">
                <ShieldCheck className="w-4 h-4 text-teal-600 flex-none mt-0.5" />
                <span>
                  Tu valoración es anónima. El profesional verá lo que escribas, pero no
                  de quién viene.
                </span>
              </div>

              <Button
                type="submit"
                disabled={enviando || puntaje === 0}
                className="w-full mt-6 bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6 disabled:opacity-50"
                data-testid="enviar-valoracion"
              >
                {enviando ? 'Enviando…' : 'Enviar valoración'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ValorarPage;
